import type { RiskType } from '@/domains/academicRisk/academicRiskAction.entity';
import type { AcademicRiskActionRepository } from '@/domains/academicRisk/academicRiskAction.repository';
import type { ResultUIStudentAcademicRisk } from '@/domains/academicRisk/academicRiskStudent.entity';
import type { PdfStudentRiskReportConfig } from '@/domains/export/pdfStudentRiskReport.entity';
import { PdfStudentRiskReportGeneratorService } from '@/infra/services/pdfStudentRiskReportGenerator.service';

export interface ExportStudentRiskReportToPdfUseCaseParams {
  studentData: ResultUIStudentAcademicRisk;
  riskTypes: RiskType[];
  generatedBy: string;
  filename?: string;
  logo?: string;
}

/**
 * UseCase para exportar reporte completo de riesgos académicos de un estudiante a PDF
 *
 * Responsabilidades:
 * - Validar datos del estudiante y usuario generador del reporte
 * - Obtener tipos de riesgo si no se proporcionan
 * - Obtener y ordenar acciones del estudiante por fecha descendente
 * - Consolidar toda la información en un reporte completo
 * - Generar PDF con formato estructurado y profesional
 */
export class ExportStudentRiskReportToPdfUseCase {
  private readonly pdfGenerator: PdfStudentRiskReportGeneratorService;
  private readonly actionRepository: AcademicRiskActionRepository;

  constructor(actionRepository: AcademicRiskActionRepository) {
    this.pdfGenerator = new PdfStudentRiskReportGeneratorService();
    this.actionRepository = actionRepository;
  }

  /**
   * Ejecuta la exportación del reporte de riesgos a PDF
   * @param params - Parámetros de exportación (datos estudiante, tipos de riesgo, metadatos)
   */
  async execute(
    params: ExportStudentRiskReportToPdfUseCaseParams,
  ): Promise<void> {
    let { studentData, riskTypes, generatedBy, filename, logo } = params;

    if (!studentData) {
      throw new Error('No se proporcionó información del estudiante');
    }

    if (!generatedBy) {
      throw new Error(
        'No se proporcionó el nombre del usuario que genera el reporte',
      );
    }

    if (!riskTypes || riskTypes.length === 0) {
      try {
        riskTypes = await this.actionRepository.getRiskTypes();
      } catch (error) {
        console.warn('No se pudieron cargar los tipos de riesgo:', error);
        riskTypes = [];
      }
    }

    const actions = await this.fetchStudentActions(
      studentData.cod_incorporacion_estudiante,
    );

    const config: PdfStudentRiskReportConfig = {
      studentData,
      actions,
      riskTypes,
      generatedBy,
      generatedDate: new Date().toLocaleString('es-CL'),
    };

    const generatedFilename = filename || this.generateFilename(studentData);

    await this.pdfGenerator.exportStudentRiskReportToPdf({
      config,
      filename: generatedFilename,
      logo,
    });
  }

  /**
   * Genera un Blob del PDF sin descargarlo
   * @param params - Parámetros de exportación
   */
  async generateBlob(
    params: ExportStudentRiskReportToPdfUseCaseParams,
  ): Promise<Blob> {
    let { studentData, riskTypes, generatedBy, logo } = params;

    if (!studentData) {
      throw new Error('No se proporcionó información del estudiante');
    }

    if (!generatedBy) {
      throw new Error(
        'No se proporcionó el nombre del usuario que genera el reporte',
      );
    }

    if (!riskTypes || riskTypes.length === 0) {
      try {
        riskTypes = await this.actionRepository.getRiskTypes();
      } catch (error) {
        console.warn('No se pudieron cargar los tipos de riesgo:', error);
        riskTypes = [];
      }
    }

    const actions = await this.fetchStudentActions(
      studentData.cod_incorporacion_estudiante,
    );

    const config: PdfStudentRiskReportConfig = {
      studentData,
      actions,
      riskTypes,
      generatedBy,
      generatedDate: new Date().toLocaleString('es-CL'),
    };

    return await this.pdfGenerator.generateStudentRiskReportBlob({
      config,
      logo,
    });
  }

  private async fetchStudentActions(cod_incorporacion_estudiante: string) {
    try {
      const response = await this.actionRepository.getActions(
        cod_incorporacion_estudiante,
      );

      const actions = response.acciones.flatMap((action) =>
        action.incorporaciones.map((inc) => ({
          cod_accion: action.cod_accion,
          fecha: inc.fecha,
          responsable: inc.funcionario_accion,
          cod_riesgo: action.cod_riesgo,
          cod_tipo_accion: action.cod_tipo_accion,
          desc_tipo_accion: action.desc_tipo_accion,
          estado_accion: inc.estado_accion,
          observaciones: inc.desc_incorporacion_accion,
          acuerdos: inc.acuerdo,
          cod_incorporacion_accion: inc.cod_incorporacion_accion,
        })),
      );

      return this.sortByDateDesc(actions);
    } catch (error) {
      console.warn(
        'No se pudieron obtener las acciones del estudiante:',
        error,
      );
      return [];
    }
  }

  private sortByDateDesc(actions: any[]) {
    return actions.sort((a, b) => {
      const dateA = this.parseDate(a.fecha);
      const dateB = this.parseDate(b.fecha);
      return dateB.getTime() - dateA.getTime();
    });
  }

  private parseDate(dateStr: string): Date {
    const [day, month, year] = dateStr.split('-').map(Number);
    const fullYear = year < 100 ? 2000 + year : year;
    return new Date(fullYear, month - 1, day);
  }

  private generateFilename(studentData: ResultUIStudentAcademicRisk): string {
    const date = new Date().toISOString().split('T')[0];
    return `reporte_riesgos_academicos_${studentData.run}_${date}.pdf`;
  }
}
